import { Head, Link } from '@inertiajs/react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import SyntaxHighlighter from 'react-syntax-highlighter'
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs'

export default function TaggingFiles() {
  return (
    <div className="container mx-auto py-6 px-4 max-w-6xl">
      <Head title="Tagging Files - Documentation" />
      
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Tagging Files</h1>
        <p className="text-lg text-gray-600 mb-4">
          Organize your uploaded files with tags for easy categorization and retrieval. Tags help you group related files and filter them in your dashboard.
        </p>
        
        <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">
                <strong>TLDR:</strong> All files uploaded through a widget with a <code className="bg-green-100 px-1 py-0.5 rounded text-green-800">tag</code> attribute will automatically be tagged with that value.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Basic Tagging</CardTitle>
          <CardDescription>
            Add the tag attribute to your simple-file-upload widget to automatically tag all uploaded files
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-2">HTML Implementation</h3>
            <p className="text-gray-600 mb-3">
              Add the <code className="bg-gray-100 px-2 py-1 rounded">tag</code> attribute to your widget:
            </p>
            <SyntaxHighlighter
              language="html"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                fontSize: '.8rem',
                borderRadius: '6px'
              }}
            >
              {`<simple-file-upload 
  public-key="yourPublicKey"
  tag="avatar"
></simple-file-upload>`}
            </SyntaxHighlighter>
            <p className="text-sm text-gray-500 mt-2">
              All files uploaded through this widget will be tagged with "avatar"
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">Multiple Widgets with Different Tags</h3>
            <p className="text-gray-600 mb-3">
              You can use multiple widgets on the same page with different tags for different purposes:
            </p>
            <SyntaxHighlighter
              language="html"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                fontSize: '.8rem',
                borderRadius: '6px'
              }}
            >
              {`<!-- Profile photo uploader -->
<simple-file-upload 
  public-key="yourPublicKey"
  tag="profile-photo"
  max-files="1"
></simple-file-upload>

<!-- Document uploader -->
<simple-file-upload 
  public-key="yourPublicKey"
  tag="documents"
  max-files="10"
></simple-file-upload>

<!-- Gallery uploader -->
<simple-file-upload 
  public-key="yourPublicKey"
  tag="gallery"
></simple-file-upload>`}
            </SyntaxHighlighter>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Tag Naming Best Practices</CardTitle>
          <CardDescription>
            Guidelines for creating effective and consistent tag names
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-green-600 mb-2">✅ Good Tag Names</h4>
              <ul className="space-y-1 text-sm">
                <li><code className="bg-gray-100 px-2 py-1 rounded">profile-photo</code></li>
                <li><code className="bg-gray-100 px-2 py-1 rounded">product-images</code></li>
                <li><code className="bg-gray-100 px-2 py-1 rounded">user-documents</code></li>
                <li><code className="bg-gray-100 px-2 py-1 rounded">gallery</code></li>
                <li><code className="bg-gray-100 px-2 py-1 rounded">invoices-2024</code></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-red-600 mb-2">❌ Avoid These</h4>
              <ul className="space-y-1 text-sm">
                <li><code className="bg-gray-100 px-2 py-1 rounded">Profile Photo!</code> (spaces, special chars)</li>
                <li><code className="bg-gray-100 px-2 py-1 rounded">IMAGES</code> (all caps)</li>
                <li><code className="bg-gray-100 px-2 py-1 rounded">img</code> (too generic)</li>
                <li><code className="bg-gray-100 px-2 py-1 rounded">user_files_for_the_main_gallery_section</code> (too long)</li>
              </ul>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium mb-2">Recommendations:</h4>
            <ul className="text-sm space-y-1 text-gray-600">
              <li>• Use lowercase letters and hyphens</li>
              <li>• Keep tags descriptive but concise</li>
              <li>• Use consistent naming patterns across your application</li>
              <li>• Consider including dates or versions when relevant</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Viewing Tagged Files</CardTitle>
          <CardDescription>
            How to filter and view your tagged files in the dashboard
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-gray-600">
              Once your files are tagged, you can easily filter and manage them in your <Link href="/dashboard" className="text-blue-600 hover:text-blue-800 underline" preserveScroll>Simple File Upload dashboard</Link>:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="text-blue-600 border-blue-600">Dashboard</Badge>
                </div>
                <p className="text-sm text-gray-600">
                  View all your files organized by tags in the main dashboard
                </p>
              </div>
              
              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="text-green-600 border-green-600">Filter</Badge>
                </div>
                <p className="text-sm text-gray-600">
                  Use the tag filter to show only files with specific tags
                </p>
              </div>
              
              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="text-purple-600 border-purple-600">Search</Badge>
                </div>
                <p className="text-sm text-gray-600">
                  Search for files by tag name to quickly find what you need
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
